import { create } from 'zustand';
import { mergeDeepRight } from 'ramda';
import Helpers from '../../js/helpers';
import api from '../../api';
import { apiGetAllPaginatedCollections } from '../../js/api';
import Server from '../../js/config/_server';
import type { CarModel, CarType, DistributedMedium, Aggregations } from '../../api/types/index';
import { SEARCH_RESOURCES, RESPONSE } from '../../js/_constants';
import useUserStore from '../../store/slices/userStore';

// --- Helpers ---
const transformToSearchParams = (filters: any) => {
    const transformed = {
        ...filters,
        tag: filters.tags?.join(','),
        carType: filters.carTypes?.join(','),
        carModel: filters.carModels?.join(','),
        kvps: filters.kvps?.join(','),
        'productionYear[gte]': filters.modelYears?.low,
        'productionYear[lte]': filters.modelYears?.high
    };
    ['carModels', 'carTypes', 'modelYears', 'tags'].forEach(k => delete transformed[k]);
    return transformed;
};

const currentSearch = Helpers.getQueryParameter();

const parseParams = (params: any) => ({
    page: params.page ? +params.page : 1,
    query: params.query || '',
    title: params.title || '',
    tags: params.tag ? params.tag.split(',') : [],
    kvps: params.kvps ? params.kvps.split(',') : [],
    carTypes: params.carType ? params.carType.split(',') : [],
    carModels: params.carModel ? params.carModel.split(',') : [],
    modelYears: {
        high: params['productionYear[lte]'] || '',
        low: params['productionYear[gte]'] || ''
    },
    expiredDate: params.expiredDate || ''
});

const initialFilterState = {
    page: 1,
    query: '',
    expiredDate: '',
    title: '',
    tags: [],
    kvps: [],
    carTypes: [],
    carModels: [],
    modelYears: { high: '', low: '' }
};

// --- Store Interface ---
interface SearchState {
    aggregations: any;
    isSearching: boolean;
    allCarModels: CarModel[];
    allCarTypes: CarType[];
    results: DistributedMedium[];
    search: typeof initialFilterState;
    previewSearch: typeof initialFilterState;
    totalItems: number | null;
    totalPages: number;
    searchQuery: string;
    isFromSearchInput: boolean;
    searchResource: string;
    
    // Actions
    doSearch: (filters: Partial<typeof initialFilterState>) => Promise<void>;
    getFilterPreview: (selectedFilters: typeof initialFilterState) => Promise<void>;
    getCarModelsAndCarTypes: () => Promise<void>;
    resetSearch: () => void;
    setSearchQuery: (query: string) => void;
    setIsSearching: (val: boolean) => void;
    setSearchResource: (resource: string) => void;
    setIsFromSearchInput: (val: boolean) => void;
}

const useSearchStore = create<SearchState>()((set, get) => ({
    aggregations: [],
    isSearching: false,
    allCarModels: [],
    allCarTypes: [],
    results: [],
    search: parseParams(currentSearch),
    previewSearch: parseParams(currentSearch),
    totalItems: null,
    totalPages: 1,
    searchQuery: currentSearch.query || '',
    isFromSearchInput: false,
    searchResource: SEARCH_RESOURCES.MEDIA,

    doSearch: async (filters) => {
        set({ isSearching: true });
        
        const mergedSearch = mergeDeepRight(get().search, filters);
        const searchParams = transformToSearchParams(mergedSearch);

        // Update URL Sync
        const url = Helpers.replaceParamsInUrl(searchParams);
        window.history.replaceState({ path: url }, '', url);

        try {
            const res = await api.getSearch(searchParams);
            
            // Clean Aggregations logic
            const aggs = res[RESPONSE.AGGREGATIONS] as any;
            if (aggs?.types) { aggs.carTypes = aggs.types; delete aggs.types; }
            if (aggs?.['array_tags']) { aggs.tags = aggs['array_tags']; delete aggs['array_tags']; }

            set({
                results: res[RESPONSE.EXPAND_COLLECTION],
                search: { ...mergedSearch },
                totalPages: Math.ceil(res[RESPONSE.TOTAL] / 30),
                totalItems: res[RESPONSE.TOTAL],
                aggregations: aggs,
                previewSearch: mergedSearch,
                isSearching: false
            });
        } catch (e) {
            set({ isSearching: false });
        }
    },

    getFilterPreview: async (selectedFilters) => {
        const searchParams = transformToSearchParams(
            mergeDeepRight(get().previewSearch, selectedFilters)
        );

        const res = await api.getSearch(searchParams);
        const aggs = res[RESPONSE.AGGREGATIONS] as any;
        
        if (aggs?.types) { aggs.carTypes = aggs.types; delete aggs.types; }
        if (aggs?.['array_tags']) { aggs.tags = aggs['array_tags']; delete aggs['array_tags']; }

        set({ previewSearch: selectedFilters, aggregations: aggs });
    },

    getCarModelsAndCarTypes: async () => {
        const { details } = useUserStore.getState();
        const brands = details.userGrantedBrands || [];

        const fetchAll = (path: string) => Promise.all(
            brands.map(b => apiGetAllPaginatedCollections(`${Server.baseUrl}/brands/${b}${path}`, { params: { pageSize: 200 } }))
        );

        const [modelsRes, typesRes] = await Promise.all([
            fetchAll('/car-models'),
            fetchAll('/car-types')
        ]);

        const carModels = (modelsRes.flat() as CarModel[]).map(m => ({
            ...m, name: m.model, key: m.model.toLowerCase().replace(/ /g, "_")
        }));

        const carTypes = (typesRes.flat() as CarType[]).map(t => ({
            ...t, name: t.type, key: t.type.toLowerCase().replace(/ /g, "_")
        }));

        // Link types to models
        carModels.forEach(m => {
            m.carTypes = carTypes.filter(t => t.model === m[RESPONSE.ID]);
        });

        set({ allCarModels: carModels, allCarTypes: carTypes });
    },

    resetSearch: () => set((state) => ({ 
        search: { ...state.search, ...initialFilterState } 
    })),

    setSearchQuery: (searchQuery) => set({ searchQuery }),
    setIsSearching: (isSearching) => set({ isSearching }),
    setSearchResource: (searchResource) => set({ searchResource }),
    setIsFromSearchInput: (isFromSearchInput) => set({ isFromSearchInput })
}));

export default useSearchStore;
